import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, ChevronsUpDown } from 'lucide-react';

import { Button } from 'components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Command, CommandGroup, CommandItem, CommandList } from 'components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { cn } from 'lib/utils';
import { useGetAgents } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IToolDetails, IToolSummaryPayload } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { getToolActions } from './tool-detail-utils';
import { ToolDashboardChart } from './tool-dashboard-chart';
import { ToolOverviewCards } from './tool-overview-cards';

// Mirrors the CallType written on each tool-execution log: agent traffic is
// `agent_execution` (direct) or `mcp_service_tool` (Auto-auth, via the runner);
// `manual_test` is the tool tester.
const CALL_TYPE_OPTIONS = [
  { value: 'agent_execution', labelKey: 'CALL_TYPE_AGENT_EXECUTION', defaultLabel: 'Agent execution' },
  { value: 'mcp_service_tool', labelKey: 'CALL_TYPE_MCP_SERVICE_TOOL', defaultLabel: 'Service tool' },
  { value: 'manual_test', labelKey: 'CALL_TYPE_MANUAL_TEST', defaultLabel: 'Manual test' },
] as const;

export function ToolDashboard({ tool }: { tool: IToolDetails }) {
  const { t } = useTranslation();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const { data: agentsData } = useGetAgents({
    limit: 100,
    offset: 0,
    project_key: projectKey,
  });

  const [selectedAgentId, setSelectedAgentId] = useState('all');
  const [selectedActionId, setSelectedActionId] = useState('all');
  // Empty = every call type; the API omits the filter entirely in that case.
  const [selectedCallTypes, setSelectedCallTypes] = useState<string[]>([]);
  const [period, setPeriod] = useState<'24h' | '7d' | '30d'>('24h');
  const [totalCalls, setTotalCalls] = useState(0);
  const [successfulCalls, setSuccessfulCalls] = useState(0);
  const [failedCalls, setFailedCalls] = useState(0);

  const actions = useMemo(
    () =>
      getToolActions(tool).map((action) => ({
        id: action.actionId,
        name: action.Name || action.actionId,
      })),
    [tool]
  );

  const { startDate, endDate } = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);

    const start = new Date(now);
    const end = new Date(now);
    end.setHours(23, 59, 59, 999);

    if (period === '7d') {
      start.setDate(now.getDate() - 6);
    } else if (period === '30d') {
      start.setDate(now.getDate() - 29);
    }

    return {
      startDate: start.toISOString(),
      endDate: end.toISOString(),
    };
  }, [period]);

  const toggleCallType = useCallback((value: string) => {
    setSelectedCallTypes((previous) =>
      previous.includes(value) ? previous.filter((item) => item !== value) : [...previous, value]
    );
  }, []);

  const callTypeLabel = useMemo(() => {
    if (selectedCallTypes.length === 0) return t('CALL_TYPE_ALL') || 'Call type: All';
    if (selectedCallTypes.length === 1) {
      const option = CALL_TYPE_OPTIONS.find((item) => item.value === selectedCallTypes[0]);
      return option ? t(option.labelKey) || option.defaultLabel : selectedCallTypes[0];
    }
    return `${t('CALL_TYPE') || 'Call type'}: ${selectedCallTypes.length}`;
  }, [selectedCallTypes, t]);

  const toolSummaryPayload: IToolSummaryPayload = useMemo(
    () => ({
      tool_id: tool.id,
      project_key: projectKey,
      start_date: startDate,
      end_date: endDate,
      ...(selectedAgentId !== 'all' ? { agent_id: selectedAgentId } : {}),
      ...(selectedActionId !== 'all' ? { action_id: selectedActionId } : {}),
      ...(selectedCallTypes.length ? { call_type: selectedCallTypes } : {}),
    }),
    [endDate, projectKey, selectedActionId, selectedAgentId, selectedCallTypes, startDate, tool.id]
  );

  return (
    <div className="relative flex w-full flex-col gap-6">
      <Card className="flex w-full flex-col">
        <CardHeader className="gap-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex w-full items-center justify-between lg:w-auto">
              <CardTitle>{t('OVERVIEW') || 'Overview'}</CardTitle>
            </div>

            <div className="grid w-full grid-cols-1 gap-2 md:grid-cols-2 lg:flex lg:flex-1 lg:flex-row lg:items-center lg:justify-end">
              <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                <SelectTrigger className="w-full lg:w-[180px]">
                  <SelectValue placeholder={t('AGENT_ALL') || 'Agent: All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('AGENT_ALL') || 'Agent: All'}</SelectItem>
                  {(agentsData?.agents || []).map((agent) => (
                    <SelectItem key={agent.id} value={agent.id}>
                      {agent.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={selectedActionId} onValueChange={setSelectedActionId}>
                <SelectTrigger className="w-full lg:w-[180px]">
                  <SelectValue placeholder={t('API_ALL') || 'API: All'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('API_ALL') || 'API: All'}</SelectItem>
                  {actions.map((action) => (
                    <SelectItem key={action.id} value={action.id}>
                      {action.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    role="combobox"
                    className="w-full justify-between font-normal lg:w-[180px]"
                  >
                    <span className="truncate">{callTypeLabel}</span>
                    <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-[220px] p-0" align="end">
                  <Command>
                    <CommandList>
                      <CommandGroup>
                        <CommandItem onSelect={() => setSelectedCallTypes([])}>
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedCallTypes.length === 0 ? 'opacity-100' : 'opacity-0'
                            )}
                          />
                          {t('CALL_TYPE_ALL') || 'Call type: All'}
                        </CommandItem>
                        {CALL_TYPE_OPTIONS.map((option) => (
                          <CommandItem
                            key={option.value}
                            onSelect={() => toggleCallType(option.value)}
                          >
                            <Check
                              className={cn(
                                'mr-2 h-4 w-4',
                                selectedCallTypes.includes(option.value) ? 'opacity-100' : 'opacity-0'
                              )}
                            />
                            {t(option.labelKey) || option.defaultLabel}
                          </CommandItem>
                        ))}
                      </CommandGroup>
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>

              <Select value={period} onValueChange={(value) => setPeriod(value as '24h' | '7d' | '30d')}>
                <SelectTrigger className="w-full lg:w-[180px]">
                  <SelectValue placeholder={t('SELECT_PERIOD') || 'Select period'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="24h">{t('LAST_24_HOURS') || 'Last 24 hour'}</SelectItem>
                  <SelectItem value="7d">{t('LAST_7_DAYS') || 'Last 7 days'}</SelectItem>
                  <SelectItem value="30d">{t('LAST_30_DAYS') || 'Last 30 days'}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <ToolOverviewCards
            totalCalls={totalCalls}
            successfulCalls={successfulCalls}
            failedCalls={failedCalls}
          />

          <CardTitle className="p-0">{t('REQUESTS') || 'Requests'}</CardTitle>
        </CardHeader>

        <CardContent className="h-[320px] min-h-[320px]">
          <ToolDashboardChart
            toolSummaryPayload={toolSummaryPayload}
            period={period}
            setTotalCalls={setTotalCalls}
            setSuccessfulCalls={setSuccessfulCalls}
            setFailedCalls={setFailedCalls}
          />
        </CardContent>
      </Card>
    </div>
  );
}
